import React, { useState } from 'react';
import { Globe } from 'lucide-react';
import { translations, Translations } from '../utils/translations';
import { useStore } from '../store/useStore';

interface SearchProps {
  onSearchModeChange: (isActive: boolean) => void;
  disabled?: boolean;
}

export const Search: React.FC<SearchProps> = ({ onSearchModeChange, disabled }) => {
  const [isActive, setIsActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSearchClick = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent form submission
    e.stopPropagation(); // Stop event bubbling
    
    if (disabled) return;
    
    const newIsActive = !isActive;
    setIsActive(newIsActive);
    onSearchModeChange(newIsActive);
    setError(null);
  };

  return (
    <div className="relative group">
      <button
        onClick={handleSearchClick}
        disabled={disabled}
        className={`p-2.5 rounded-xl transition-colors focus:outline-none ${
          isActive
            ? 'bg-blue-100 dark:bg-blue-900'
            : disabled
            ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed'
            : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600'
        }`}
        aria-label={isActive ? 'Disable search mode' : 'Enable search mode'}
        title={isActive ? 'Disable search mode' : 'Enable search mode'}
      >
        <Globe
          className={`w-5 h-5 ${
            isActive
              ? 'text-blue-500 dark:text-blue-400'
              : 'text-gray-700 dark:text-gray-300'
          }`}
        />
      </button>

      {error && (
        <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 px-4 py-2 rounded-md text-sm border border-red-200 dark:border-red-800 whitespace-nowrap">
          {error}
        </div>
      )}

      <div
        className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-gray-800 text-white text-sm py-1 px-2 rounded-md pointer-events-none whitespace-nowrap"
        role="tooltip"
      >
        {disabled
          ? translations[useStore.getState().language as keyof Translations]?.selectModelToStart
          : isActive
            ? translations[useStore.getState().language as keyof Translations]?.webSearchEnabled
            : translations[useStore.getState().language as keyof Translations]?.enableWebSearch}
      </div>
    </div>
  );
};